import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import MarkdownText from '../../components/foundations/MarkdownText';
import useTypingAnimation from '../hooks/useTypingAnimation';
import { MESSAGE_ROLES, ARTIFACT_TYPES } from '../constants';
import css from './ChatMessage.css';
import ArtifactWithInsights from './artifacts/ArtifactWithInsights';
import EphemeralUpdatesPanel from './EphemeralUpdatesPanel';

const ROLE_LABELS = {
    USER: 'You',
    ASSISTANT: 'AI Assistant',
};

const TEXT_MESSAGE_TYPE = 'markdown';

const ChatMessage = ({ message, onDashboardClick }) => {
    const isUser = message.role === MESSAGE_ROLES.USER;
    const isAssistant = message.role === MESSAGE_ROLES.ASSISTANT;
    const { isStreaming } = message;

    // Check if content is empty (for initial thinking state)
    const isInitialThinking =
        message.isThinking && (!message.content || (Array.isArray(message.content) && message.content.length === 0));

    const formattedTime = useMemo(() => (message.createdAt ? new Date(message.createdAt).toLocaleTimeString() : ''), [
        message.createdAt,
    ]);

    // Get current markdown content for typing animation (only for last markdown block in streaming)
    const currentTextForAnimation = useMemo(() => {
        if (!isAssistant || !isStreaming || !Array.isArray(message.content)) return '';

        for (let i = message.content.length - 1; i >= 0; i--) {
            if (message.content[i].type === TEXT_MESSAGE_TYPE) {
                return message.content[i].data || '';
            }
        }
        return '';
    }, [isAssistant, isStreaming, message.content]);

    // Use typing animation hook for assistant messages
    const animatedText = useTypingAnimation(currentTextForAnimation, isAssistant && isStreaming);

    const contentBlocks = useMemo(() => {
        if (!Array.isArray(message.content)) return [];

        return message.content.map((block, index) => {
            const isLastTextBlock =
                isStreaming &&
                block.type === TEXT_MESSAGE_TYPE &&
                index === message.content.findLastIndex(b => b.type === TEXT_MESSAGE_TYPE);
            const { data, ...rest } = block;

            return {
                ...rest,
                data: isLastTextBlock ? String(animatedText || '') : data,
                id: block.id || `${message.id}-block-${index}`,
            };
        });
    }, [message.content, message.id, isStreaming, animatedText]);

    const messageContentBlocks = contentBlocks.filter(block => !block.is_final_analysis);
    const previewContentBlocks = contentBlocks.filter(block => block.is_final_analysis);

    return (
        <div className={classNames(css.chatMessage, { [css.user]: isUser, [css.assistant]: isAssistant })}>
            <div className={css.chatMessageHeader}>
                <div className={css.chatMessageAvatar}>
                    <Icon name={isUser ? 'user' : 'robot'} size={IconSizes.SMALL} />
                </div>
                <div className={css.chatMessageRole}>{isUser ? ROLE_LABELS.USER : ROLE_LABELS.ASSISTANT}</div>
                {message.isThinking && (
                    <span className={css.chatMessageThinkingBadge}>
                        <Icon name="loader" size={IconSizes.X_SMALL} />
                        Thinking...
                    </span>
                )}
                <div className={css.chatMessageTime}>{formattedTime}</div>
            </div>
            <div className={css.chatMessageContent}>
                {isInitialThinking ? (
                    <div className={css.chatMessageThinking}>
                        <Icon name="loader" size={IconSizes.SMALL} className={css.chatMessageThinkingIcon} />
                        <span>Analyzing your query...</span>
                    </div>
                ) : (
                    <>
                        {isUser && message.content && (
                            <MarkdownText
                                content={
                                    Array.isArray(message.content)
                                        ? String(message.content.find(b => b.type === TEXT_MESSAGE_TYPE)?.data || '')
                                        : String(message.content || '')
                                }
                            />
                        )}
                        {isAssistant && message.ephemeralUpdates?.length > 0 && (
                            <EphemeralUpdatesPanel updates={message.ephemeralUpdates} />
                        )}
                        {isAssistant && messageContentBlocks.length > 0 && (
                            <>
                                {messageContentBlocks.map(block => {
                                    const isWidget = block.type === ARTIFACT_TYPES.WIDGET;
                                    return (
                                        <div key={block.id} className={isWidget ? css.widget : css.artifact}>
                                            <ArtifactWithInsights artifact={block} />
                                        </div>
                                    );
                                })}
                                {previewContentBlocks.length > 0 && (
                                    <div
                                        className={css.previewDashboard}
                                        onClick={() => onDashboardClick(previewContentBlocks)}
                                    >
                                        <span>View Dashboard</span>
                                        <Icon
                                            name="arrow-right-fill"
                                            size={IconSizes.SMALL}
                                            variant={IconVariants.ACTIVE}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

ChatMessage.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
        content: PropTypes.array, // Array of content blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
        createdAt: PropTypes.instanceOf(Date),
        isThinking: PropTypes.bool,
        isStreaming: PropTypes.bool,
        ephemeralUpdates: PropTypes.array,
        steps: PropTypes.array,
    }).isRequired,
    onDashboardClick: PropTypes.func,
};

export default ChatMessage;
